import { useState, FormEvent, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { login, getCurrentUser } from '@/lib/api/auth'
import { setAuthSession, getAuthToken, setUser, clearAuth } from '@/lib/auth/session'
import { Button, Input, Label, Logo } from '@rr/ui'

export default function LoginPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Validate any stored token before redirecting. A token presence check
    // alone bounces the user to "/" with a stale or expired token, which
    // then 401s on every page load. Hit /auth/me — if it succeeds, refresh
    // the cached user and redirect; if not, clear the session and stay on
    // the login form.
    let cancelled = false
    const token = getAuthToken()
    if (!token) return
    ;(async () => {
      try {
        const res: any = await getCurrentUser()
        const me = res?.data ?? res
        if (cancelled) return
        if (me && (me.id || me.email)) {
          setUser(me)
          navigate('/')
        } else {
          clearAuth()
        }
      } catch {
        if (!cancelled) clearAuth()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [navigate])

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setErrors({})

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const response = await login(formData)

      // Store auth session (access + refresh + user). Admin is an internal
      // tool — keep the session sticky across browser restarts.
      setAuthSession(response.accessToken, response.refreshToken, response.user, true)

      // Redirect to dashboard
      navigate('/')
    } catch (error: any) {
      setErrors({
        general: error.message || 'Login failed. Please check your credentials.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand — mirrors the topbar lockup (logo + name + Admin badge). */}
        <div className="mb-8 flex items-center justify-center gap-2 text-primary">
          <Logo tone="current" size={32} label="Route and Route" />
          <span className="text-2xl font-semibold tracking-tight">
            Route and Route
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border rounded px-1.5 py-0.5 ml-1">
            Admin
          </span>
        </div>

        {/* Login Form */ }
        <div className="bg-card rounded-lg border border-border p-8">
          <form onSubmit={ handleSubmit } className="space-y-5">
            { errors.general && (
              <div className="bg-danger/10 border border-danger/20 rounded-lg p-3">
                <p className="text-sm text-danger">{ errors.general }</p>
              </div>
            ) }

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={ formData.email }
                onChange={ (e) => setFormData({ ...formData, email: e.target.value }) }
                placeholder="admin@example.com"
                autoComplete="email"
                disabled={ loading }
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={ formData.password }
                onChange={ (e) => setFormData({ ...formData, password: e.target.value }) }
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={ loading }
                className={errors.password ? "border-red-500" : ""}
              />
              {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={ loading }
            >
              { loading ? 'Signing In...' : 'Sign In' }
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
